import type {
    AddDonationRequestBody,
    AddItemRequestBody,
    CartUI,
    CartCreateBody,
    UpdateItemQtyRequestBody,
    UpdateItemStoreRequestBody,
    GuestEmailRequestBody,
    PickupInfoRequestBody,
    AddBillingAddressRequestBody,
    AddShippingAddressRequestBody,
    ApplyPromoCodeRequestBody,
    AddPaymentMethodRequestBody,
    PlaceOrderRequestBody,
    Order,
    OrderRedirect,
    UpdateItemFulfillmentMethodRequestBody,
    TreatPointRequestBody,
    ShippingMethods,
    EstimatedTreatsPoints,
    DeliveryOptions,
    DeliveryOptionsPost,
    ShippingAddressValidationResult,
    SetShippingMethodRequestBody,
    UpdateShippingAddressRequestBody,
    AddPrescriptionBody,
    CartValidationResponse,
} from '@libs/common-node';
import type { Nullable } from '@libs/core';
import { AUTH_COOKIES, AUTH_HEADERS, HttpMethod } from '@libs/core';
import fetchCart, { getHeaders } from './fetchCart';
import { getNextApiRoute } from './api';
import { typedFetch } from './typedFetch';
import {
    CartValidationError,
    isCartValidationErrorResponse,
} from '../providers/utils/error';
import { getCookieValue } from './getCookieValue';

const CART_BASE_URL = getNextApiRoute('carts');

export const getEstimatedTreatsPointsForCart = async ({
    cartId,
    loyaltyId,
}: {
    cartId: string;
    loyaltyId?: number;
}): Promise<Nullable<EstimatedTreatsPoints>> => {
    const url = getNextApiRoute(`loyalty/v2/points/estimate/${cartId}`);
    const headers: HeadersInit = {};

    if (loyaltyId) {
        headers[AUTH_HEADERS.X_PETM_LOYALTY_NUMBER] = `${loyaltyId}`;
    }

    return await typedFetch<EstimatedTreatsPoints>(url, { headers });
};

export const createCart = async ({ body }: { body: CartCreateBody }) =>
    fetchCart<CartUI | null>(CART_BASE_URL, HttpMethod.POST, body);

export const getCartById = async ({ cartId }: { cartId: string }) =>
    fetchCart<CartUI | null>(
        `${CART_BASE_URL}/${cartId}`,
        HttpMethod.GET,
        undefined,
        {},
        [303, 400],
        {
            cache: 'no-store',
        }
    );

export const addItem = async ({
    cartId,
    version,
    body,
}: {
    cartId?: string;
    version?: number;
    body: AddItemRequestBody;
}) => {
    const url =
        cartId && version
            ? `${CART_BASE_URL}?id=${cartId}&version=${version}`
            : CART_BASE_URL;

    return fetchCart<CartUI | null>(url, HttpMethod.POST, body);
};

export const updateItemQty = async ({
    cartId,
    itemId,
    version,
    body,
}: {
    cartId: string;
    itemId: string;
    version: number;
    body: UpdateItemQtyRequestBody;
}) =>
    fetchCart<CartUI | null>(
        `${CART_BASE_URL}/${cartId}/items/${itemId}?version=${version}`,
        HttpMethod.PUT,
        body
    );

export const updateSubscriptionStatus = async ({
    cartId,
    version,
}: {
    cartId: string;
    version: number;
}) =>
    fetchCart<CartUI | null>(
        `${CART_BASE_URL}/${cartId}/update-subscriptions-status?version=${version}`,
        HttpMethod.POST
    );

export const updateItemFulfillmentMethod = async ({
    cartId,
    itemId,
    version,
    body,
}: {
    cartId: string;
    itemId: string;
    version: number;
    body: UpdateItemFulfillmentMethodRequestBody;
}) =>
    fetchCart<CartUI | null>(
        `${CART_BASE_URL}/${cartId}/items/${itemId}/fulfillment-method?version=${version}`,
        HttpMethod.PUT,
        body
    );

export const addBillingAddress = async ({
    cartId,
    version,
    body,
}: {
    cartId: string;
    version: number;
    body: AddBillingAddressRequestBody;
}) =>
    fetchCart<CartUI | null>(
        `${CART_BASE_URL}/${cartId}/billing-addresses?version=${version}`,
        HttpMethod.POST,
        body
    );

export const addShippingAddress = async ({
    cartId,
    version,
    body,
}: {
    cartId: string;
    version: number;
    body: AddShippingAddressRequestBody;
}) =>
    await fetchCart<CartUI | null>(
        `${CART_BASE_URL}/${cartId}/shipping-addresses?version=${version}`,
        HttpMethod.POST,
        body
    );

export const updateShippingAddress = async ({
    cartId,
    version,
    body,
}: {
    cartId: string;
    version: number;
    body: UpdateShippingAddressRequestBody;
}) =>
    await fetchCart<CartUI | null>(
        `${CART_BASE_URL}/${cartId}/shipping-addresses?version=${version}`,
        HttpMethod.PUT,
        body
    );

export const addPayment = async ({
    cartId,
    version,
    body,
}: {
    cartId: string;
    version: number;
    body: AddPaymentMethodRequestBody;
}) => {
    if (body.giftCardNumber) {
        const token = getCookieValue(AUTH_COOKIES.RECAPTCHA_TOKEN);

        return fetchCart<CartUI | null>(
            `${CART_BASE_URL}/${cartId}/payments?version=${version}`,
            HttpMethod.POST,
            { ...body, recaptchaToken: token ?? '' },
            {},
            [303, 400, 500]
        );
    }
    return fetchCart<CartUI | null>(
        `${CART_BASE_URL}/${cartId}/payments?version=${version}`,
        HttpMethod.POST,
        body,
        {},
        [303, 400, 500]
    );
};

export const removePayment = async ({
    cartId,
    version,
    body,
}: {
    cartId: string;
    version: number;
    body: { methodId: string };
}) =>
    fetchCart<CartUI | null>(
        `${CART_BASE_URL}/${cartId}/payments/${body.methodId}?version=${version}`,
        HttpMethod.DELETE
    );

export const removeItem = async ({
    cartId,
    itemId,
    version,
}: {
    cartId: string;
    itemId: string;
    version: number;
}) =>
    fetchCart<CartUI | null>(
        `${CART_BASE_URL}/${cartId}/items/${itemId}?version=${version}`,
        HttpMethod.DELETE
    );

export const getShippingMethods = async ({ cartId }: { cartId: string }) =>
    fetchCart<ShippingMethods[] | null>(
        `${CART_BASE_URL}/${cartId}/shipping-methods`,
        HttpMethod.GET
    );

export const getDeliveryOptions = async ({ cartId }: { cartId: string }) =>
    fetchCart<DeliveryOptions | null>(
        `${CART_BASE_URL}/${cartId}/delivery-options`,
        HttpMethod.GET
    );

export const setDeliveryOptions = async ({
    cartId,
    version,
    body,
}: {
    cartId: string;
    version: number;
    body: DeliveryOptionsPost;
}) =>
    fetchCart<CartUI | null>(
        `${CART_BASE_URL}/${cartId}/delivery-options?version=${version}`,
        HttpMethod.POST,
        body
    );

export const validateShippingAddresses = async (cartId: string) =>
    fetchCart<ShippingAddressValidationResult>(
        `${CART_BASE_URL}/${cartId}/addresses`,
        HttpMethod.GET
    );

export const updateItemStore = async ({
    cartId,
    itemId,
    body,
}: {
    cartId: string;
    itemId: string;
    body: UpdateItemStoreRequestBody;
}) =>
    fetchCart<CartUI | null>(
        `${CART_BASE_URL}/${cartId}/items/${itemId}/store`,
        HttpMethod.PUT,
        body
    );

export const addDonation = async ({
    cartId,
    version,
    body,
}: {
    cartId: string;
    version: number;
    body: AddDonationRequestBody;
}) =>
    fetchCart<CartUI | null>(
        `${CART_BASE_URL}/${cartId}/donations?version=${version}`,
        HttpMethod.POST,
        body
    );

export const removeDonation = async ({
    cartId,
    version,
}: {
    cartId: string;
    version: number;
}) =>
    fetchCart<CartUI | null>(
        `${CART_BASE_URL}/${cartId}/donations?version=${version}`,
        HttpMethod.DELETE
    );

export const addDiscountCode = async ({
    cartId,
    version,
    body,
}: {
    cartId: string;
    version: number;
    body: ApplyPromoCodeRequestBody;
}) =>
    fetchCart<CartUI | null>(
        `${CART_BASE_URL}/${cartId}/discount-codes?version=${version}`,
        HttpMethod.POST,
        body
    );

export const removeDiscountCode = async ({
    cartId,
    version,
    body,
}: {
    cartId: string;
    version: number;
    body: { codeId: string };
}) =>
    fetchCart<CartUI | null>(
        `${CART_BASE_URL}/${cartId}/discount-codes/${body.codeId}?version=${version}`,
        HttpMethod.DELETE
    );

export const addGuestEmail = async ({
    cartId,
    version,
    body,
}: {
    cartId: string;
    version: number;
    body: GuestEmailRequestBody;
}) =>
    fetchCart<CartUI | null>(
        `${CART_BASE_URL}/${cartId}/email-address?version=${version}`,
        HttpMethod.POST,
        body
    );

export const addPickupInfo = async ({
    cartId,
    body,
    version,
}: {
    cartId: string;
    body: PickupInfoRequestBody;
    version: number;
}) =>
    fetchCart<CartUI | null>(
        `${CART_BASE_URL}/${cartId}/pickup?version=${version}`,
        HttpMethod.POST,
        body
    );

export const addTreatPoints = async ({
    cartId,
    body,
    version,
}: {
    cartId: string;
    body: TreatPointRequestBody & {
        loyaltyId: Nullable<number>;
    };
    version: number;
}) => {
    const { amount, loyaltyId } = body;
    return fetchCart<CartUI | null>(
        `${CART_BASE_URL}/${cartId}/treat-points?version=${version}`,
        HttpMethod.POST,
        { amount },
        {
            [AUTH_HEADERS.X_PETM_LOYALTY_NUMBER]: loyaltyId
                ? `${loyaltyId}`
                : '',
        }
    );
};

export const removeTreatPoints = async ({
    cartId,
    version,
}: {
    cartId: string;
    version: number;
}) =>
    fetchCart<CartUI | null>(
        `${CART_BASE_URL}/${cartId}/treat-points?version=${version}`,
        HttpMethod.DELETE
    );

export const placeOrder = async ({
    cartId,
    body,
    version,
}: {
    cartId: string;
    body: PlaceOrderRequestBody;
    version: number;
}) =>
    typedFetch<Order | OrderRedirect | null>(
        `${CART_BASE_URL}/order/${cartId}?version=${version}`,
        {
            method: HttpMethod.POST,
            body: JSON.stringify(body),
            headers: getHeaders(),
            additionalSuccessCodes: [422, 503, 400],
        }
    );

export const setShippingMethods = async ({
    cartId,
    version,
    body,
}: {
    cartId: string;
    version: number;
    body: SetShippingMethodRequestBody;
}) =>
    fetchCart<CartUI | null>(
        `${CART_BASE_URL}/${cartId}/shipping-methods?version=${version}`,
        HttpMethod.POST,
        body
    );

export const addLegacyPrescription = async ({
    cartId,
    itemId,
    body,
}: {
    cartId: string;
    itemId: string;
    body: AddPrescriptionBody;
}) =>
    fetchCart<CartUI | null>(
        `${CART_BASE_URL}/${cartId}/items/${itemId}/prescription`,
        HttpMethod.POST,
        body
    );

export const addPrescription = async ({
    cartId,
    itemId,
    body,
}: {
    cartId: string;
    itemId: string;
    body: AddPrescriptionBody;
}) =>
    fetchCart<CartUI | null>(
        `${CART_BASE_URL}/${cartId}/items/${itemId}/prescription/v2`,
        HttpMethod.POST,
        body
    );

export const validateCart = async ({
    cartId,
    useRealtimeInventory = false,
    throwOnError = true,
}: {
    cartId: string;
    useRealtimeInventory?: boolean;
    throwOnError?: boolean;
}) => {
    const params = new URLSearchParams();

    if (useRealtimeInventory) {
        params.set('useRealtimeInventory', 'true');
    }
    const response = await typedFetch<CartValidationResponse>(
        `${CART_BASE_URL}/${cartId}/validate?${params.toString()}`,
        {
            method: HttpMethod.PUT,
            headers: getHeaders(),
            additionalSuccessCodes: [303, 422],
        }
    );

    if (isCartValidationErrorResponse(response)) {
        if (throwOnError) {
            throw new CartValidationError(response.title, response);
        } else {
            throw new Error('Out of stock');
        }
    }
    return response;
};
